import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

export default function RecepcionStaff() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ nombre: "", email: "", password: "", activo: true });

    const load = () => api.get("/recepcion-staff").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            if (editing) {
                const payload = { nombre: form.nombre, activo: form.activo };
                if (form.password) payload.password = form.password;
                await api.patch(`/recepcion-staff/${editing}`, payload);
            } else {
                await api.post("/recepcion-staff", form);
            }
            toast.success("Guardado"); setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar ${u.nombre}?`)) return;
        try { await api.delete(`/recepcion-staff/${u.user_id}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader title="Recepción" subtitle="Personal de recepción del edificio."
                actions={<Button onClick={() => { setEditing(null); setForm({ nombre: "", email: "", password: "", activo: true }); setOpen(true); }} data-testid="rec-new"><Plus className="h-4 w-4 mr-1" /> Nuevo</Button>} />
            <div className="grid gap-2">
                {items.map((u) => (
                    <Card key={u.user_id}>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-accent text-accent-foreground grid place-items-center text-xs font-semibold">
                                    {u.nombre.slice(0, 2).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-heading font-semibold flex items-center gap-2">
                                        {u.nombre}
                                        {!u.activo && <Badge variant="secondary">Inactivo</Badge>}
                                    </p>
                                    <p className="text-xs text-muted-foreground font-mono">{u.email}</p>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => { setEditing(u.user_id); setForm({ nombre: u.nombre, email: u.email, password: "", activo: u.activo }); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="outline" onClick={() => remove(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Editar recepcionista" : "Nuevo recepcionista"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-2"><Label>Nombre</Label>
                            <Input data-testid="rec-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                        </div>
                        <div className="space-y-2"><Label>Email</Label>
                            <Input data-testid="rec-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div className="space-y-2"><Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
                            <Input data-testid="rec-pass" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch id="rec-activo" checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
                            <Label htmlFor="rec-activo">Activo</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} data-testid="rec-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
